
import React, { Fragment, useContext, useState } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	Form,
	FormGroup,
	Input,
	InputGroup,
	InputGroupText,
	Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faUser, faLockOpen, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SpinnerComponent } from 'react-element-spinner'
import { useNavigate } from 'react-router-dom'
import sweetalert from 'sweetalert2'

library.add(faUser, faLockOpen, faEyeSlash, faEye)

import AuthenticationService from '../../services/AuthenticationService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'
import Enumeration from '../../enumeration'
import { trigger } from '../../events/customEvents'
import EulaModal from './EulaModal'

const Login = () =>
{
	const { t } = useTranslation()
	const [loginRequest, setLoginRequest] = useState({ userName: '', password: '' })
	const [showPassword, setShowPassword] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [isBusy, setIsBusy] = React.useState(false)
	const navigate = useNavigate()
	const [showEulaModal, setShowEulaModal] = useState(false)
	const [pendingLoginSession, setPendingLoginSession] = useState(null)
	const [isAcceptingEula, setIsAcceptingEula] = useState(false)
	
	const handleUserNameChange = (e) =>
	{
		const { name, value} = e.target
		setLoginRequest({ ...loginRequest, [name]: value })
	}
	
	const handlePasswordChange = (e) =>
	{
		const { name, value } = e.target
		setLoginRequest({ ...loginRequest, [name]: value })
	}
	
	const handleDisplayAuthFailure = (title, message) =>
	{
		// setAlertProps({ title: title, message: message })
		// setShowAlert(true)
		sweetalert.fire({
			title: title,
			text: message,
			icon: 'error',
			timer: 3000,
			timerProgressBar: true,
			showConfirmButton: false
		});
	}
	
	const handleLogin = (e) =>
	{
		e.preventDefault()
		
		setIsBusy(true)
		AuthenticationService
			.authenticateUser(loginRequest)
			.then((result) =>
			{
				setIsBusy(false)
				
				let loginSession = result.data
				if (loginSession.isAuthenticated)
				{
					sessionContext.setSession(loginSession)
					
					if (SiteService.isInGroup(loginSession, Enumeration.UserGroups.Administrators))
					{
						navigate('/secure/business-dashboard')
					}
					else
					{
						navigate('/secure/dealer-dashboard')
					}
				}
				else
				{
					if (loginSession.authenticationResult !== undefined)
					{
						let failedMessage = ''
						
						switch (loginSession.authenticationResult.authenticationResultType)
						{
							case Enumeration.AuthenticationResultTypes.AnswerQuestion:
								failedMessage = t('Login.Failed_SecurityQuestion')
								break
							case Enumeration.AuthenticationResultTypes.ChangePassword:
								failedMessage = t('Login.Failed_ChangePassword')
								trigger('raiseNotification', { title: 'Login Failed', message: failedMessage, type: 'danger', duration: 5 })
								navigate('/auth/changePassword')
								break
							case Enumeration.AuthenticationResultTypes.ValidateEmail:
								failedMessage = t('Login.Failed_ValidateEmail')
								break
							case Enumeration.AuthenticationResultTypes.TwoFactorAuth:
							case Enumeration.AuthenticationResultTypes.UnknownDevice:
							case Enumeration.AuthenticationResultTypes.UserLockedOut:
								failedMessage = t('Login.Failed_UserLockedOut')
								break
							case Enumeration.AuthenticationResultTypes.AcceptEula:
								setPendingLoginSession(loginSession)
								setShowEulaModal(true)
								return
							case Enumeration.AuthenticationResultTypes.UnknownUser:
							case Enumeration.AuthenticationResultTypes.BadPassword:
							case Enumeration.AuthenticationResultTypes.SessionInvalid:
								failedMessage = t('Login.Failed_BadCredentials')
								break
						}
						
						handleDisplayAuthFailure(t('Login.Failed_Title'), failedMessage)
					}
				}
			})
			.catch((error) =>
			{
				setIsBusy(false)
			})
	}
	
	const handleEulaAccept = () =>
	{
		if (!pendingLoginSession) return

		setIsAcceptingEula(true)
		const sessionKey = pendingLoginSession.user?.userSession?.sessionKey || ''

		AuthenticationService
			.acceptPartnerEula(sessionKey)
			.then((result) =>
			{
				setIsAcceptingEula(false)
				setShowEulaModal(false)

				let loginSession = result.data
				if (loginSession.isAuthenticated)
				{
					sessionContext.setSession(loginSession)

					if (SiteService.isInGroup(loginSession, Enumeration.UserGroups.Administrators))
					{
						navigate('/secure/business-dashboard')
					}
					else
					{
						navigate('/secure/dealer-dashboard')
					}
				}
				else
				{
					handleDisplayAuthFailure(t('Login.Failed_Title'), t('Login.Failed_AcceptEula'))
				}

				setPendingLoginSession(null)
			})
			.catch((error) =>
			{
				setIsAcceptingEula(false)
				setShowEulaModal(false)
				setPendingLoginSession(null)
				handleDisplayAuthFailure(t('Login.Failed_Title'), t('Login.Failed_AcceptEula'))
			})
	}

	const handleEulaDecline = () =>
	{
		setShowEulaModal(false)
		setPendingLoginSession(null)
		handleDisplayAuthFailure(t('Login.Failed_Title'), t('Login.Failed_AcceptEula'))
	}

	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
				style={{ zIndex: 9999 }}
			/>
			<Fragment>
				<div className="h-100 bg-premium-dark bg-animation">
					<div className="d-flex h-100 justify-content-center align-items-center">
						<Col md="5" className="mx-auto">
							<Row >
								<h1 className="text-white" style={{ textAlign: 'center' }}>
									RPM Auctions
								</h1>
							</Row>
							
							<Card className="main-card shadow-lg border-0">
								<CardBody className="p-5">
									<div className="text-center mb-4">
										<h3 className="font-weight-bold mb-2">{ t('Login.Title') }</h3>
									</div>
									
									<Form role="form" onSubmit={ handleLogin } style={{ zIndex: 1000 }}>
										<FormGroup className="mb-3">
											<InputGroup>
												<InputGroupText>
													<FontAwesomeIcon icon={ faUser } />
												</InputGroupText>
												<Input
													autoFocus
													name="userName"
													placeholder={ t('Login.Username_Placeholder') }
													type="text"
													value={ loginRequest.userName }
													onChange={ handleUserNameChange }
												/>
											</InputGroup>
										</FormGroup>
										
										<FormGroup className="mb-3">
											<InputGroup>
												<InputGroupText>
													<FontAwesomeIcon icon={ faLockOpen } />
												</InputGroupText>
												<Input
													id="password"
													name="password"
													placeholder={ t('Login.Password_Placeholder') }
													type={ showPassword ? "text" : "password" }
													value={ loginRequest.password }
													onChange={ handlePasswordChange }
												/>
												<InputGroupText>
													<FontAwesomeIcon icon={ showPassword ? faEyeSlash : faEye } style={{ cursor: 'pointer' }} onClick={ () => setShowPassword(!showPassword) } />
												</InputGroupText>
											</InputGroup>
										</FormGroup>
	
										<Button
											color="primary"
											size="lg"
											block
											className="btn-shadow btn-hover-shine btn-wide mb-3"
										>
											{ t('Login.Sign_In_Button') }
										</Button>
	
									</Form>
								</CardBody>
							</Card>
							
							<div className="text-center text-white opacity-8 mt-4">
								<small>Copyright &copy; CDLSI 2025</small>
							</div>
						</Col>
					</div>
				</div>
			</Fragment>
			<EulaModal
				isOpen={ showEulaModal }
				onAccept={ handleEulaAccept }
				onDecline={ handleEulaDecline }
				isBusy={ isAcceptingEula }
			/>
		</>
	)
}

export default Login
